// src/components/TermsModal.jsx
// Blocks app access until user accepts Terms of Service + Privacy Policy.
// Acceptance stored in localStorage with a version number — bump CURRENT_TERMS_VERSION
// to re-prompt all existing users when terms change.

import React, { useState } from 'react';
import { T } from '../theme';

// ============================================================================
// VERSION CONTROL — bump this when legal text changes to re-prompt all users
// ============================================================================
export const CURRENT_TERMS_VERSION = '1.0';
const STORAGE_KEY = 'shopbuilder_terms_acceptance';

// ============================================================================
// PUBLIC HELPERS — used by App.js and the "view terms" link in What's New
// ============================================================================

// Check if current user has accepted the latest version
export function hasAcceptedCurrentTerms() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return data.version === CURRENT_TERMS_VERSION && data.accepted === true;
  } catch {
    return false; // localStorage corrupted or disabled — treat as not accepted
  }
}

// Clear acceptance — for revoke-consent flow
export function revokeTermsAcceptance() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage may be disabled — fail silently
  }
}

// ============================================================================
// LEGAL COPY — REVIEW WITH A LAWYER BEFORE FINAL PRODUCTION DEPLOY
// ============================================================================
const TERMS_SECTIONS = [
  {
    heading: 'What ShopBuilder Pro Is (and Is Not)',
    body: [
      'ShopBuilder Pro is a DIY shop layout planning tool. It helps users visualize possible equipment placement and create reference drawings for early-stage shop planning conversations.',
      'ShopBuilder Pro is not a CAD program, architectural drawing tool, engineering tool, code-compliance tool, or substitute for professional advice. Layouts, drawings, measurements, equipment placements, and exports produced by the tool are conceptual only.',
      'Outputs from ShopBuilder Pro are not scale-certified, not professionally reviewed, not stamped, and not suitable for permitting, construction, installation, safety certification, financing, insurance, or any decision requiring architectural, engineering, electrical, structural, fire/life-safety, accessibility, or code-compliance review.',
      'Before purchasing equipment, modifying a building, applying for permits, installing equipment, or constructing any shop space based on layouts from this tool, you must consult qualified licensed professionals, including architects, engineers, electricians, equipment installers, local building authorities, and any other professionals required for your project.'
    ]
  },
  {
    heading: 'User Responsibility',
    body: [
      'You are solely responsible for verifying all measurements, site conditions, equipment dimensions, clearances, utility requirements, anchoring requirements, ventilation requirements, drainage requirements, accessibility requirements, fire/life-safety requirements, and applicable laws, codes, standards, permits, and manufacturer instructions.',
      'You must not rely on ShopBuilder Pro as the sole basis for any purchase, construction, installation, safety, compliance, financial, or business decision.',
      'You are responsible for ensuring that any information you enter into the tool is accurate and that any output is reviewed by qualified professionals before use.'
    ]
  },
  {
    heading: 'No Warranty / Limitation of Liability',
    body: [
      'ShopBuilder Pro is provided "as is" and "as available" without warranties of any kind, whether express, implied, statutory, or otherwise.',
      'iTech does not guarantee the accuracy, completeness, reliability, suitability, scale, measurements, dimensions, clearances, equipment specifications, availability, pricing, or compatibility of any information shown in the tool or included in its exports.',
      'To the maximum extent permitted by law, iTech, its affiliates, suppliers, service providers, employees, contractors, and the creators of ShopBuilder Pro will not be liable for any direct, indirect, incidental, consequential, special, exemplary, punitive, or other losses, damages, costs, expenses, claims, delays, business interruptions, lost profits, construction issues, installation issues, compliance issues, or purchasing decisions arising from or related to use of the tool or its outputs.',
      'Some jurisdictions do not allow certain warranty exclusions or liability limitations, so some of the above limitations may not apply to you.'
    ]
  },
  {
    heading: 'AI-Generated Content',
    body: [
      'Some features use AI, including Anthropic Claude models, to interpret sketches, images, text descriptions, or other user inputs and suggest possible layouts. AI-generated outputs may contain errors, omissions, inaccurate dimensions, unsuitable placements, or fabricated assumptions.',
      'You must manually review all AI-generated layouts and have them verified by qualified professionals before relying on them for any purpose.',
      'When you use AI features, the information you provide for those features, such as text descriptions or uploaded sketches, is transmitted to third-party AI service providers for processing. Do not include confidential, proprietary, commercially sensitive, security-sensitive, or personally identifying information in AI inputs unless you are authorized to do so and accept the associated risk.',
      'AI features are provided for convenience and conceptual ideation only. They do not replace professional design, engineering, compliance, installation, or safety review.'
    ]
  },
  {
    heading: 'Data Storage & Privacy',
    body: [
      'ShopBuilder Pro stores project data locally in your browser using localStorage unless a feature expressly requires transmission, such as AI processing, export, sharing, hosting, analytics, or another connected service.',
      'Local browser storage remains on the device and browser profile where it was created. If you clear your browser site data, switch devices, use a different browser, or use private/incognito mode, saved projects and preferences may be unavailable or deleted.',
      'We may use functional storage to remember preferences, such as terms acceptance, and to enable core features. We may also collect limited technical and usage information, including anonymous or aggregated analytics, to maintain, secure, troubleshoot, and improve the tool.',
      'Third-party services we may use include Anthropic API for AI features and Vercel for hosting, deployment, performance, and analytics. These providers process information under their own terms and privacy policies.',
      'You can clear locally stored project data at any time by clearing your browser site data for the ShopBuilder Pro website. Doing so will delete locally saved projects and reset local preferences, including terms acceptance.'
    ]
  },
  {
    heading: 'Privacy Rights and Compliance',
    body: [
      'We handle personal information in accordance with applicable privacy laws, which may include Canada’s Personal Information Protection and Electronic Documents Act (PIPEDA), Alberta’s Personal Information Protection Act (PIPA), and, where applicable, the EU/EEA General Data Protection Regulation (GDPR).',
      'Depending on your location and the nature of the information involved, you may have rights to request access to personal information we hold about you, request correction of inaccurate information, withdraw consent where consent is the basis for processing, object to or restrict certain processing, request portability, or request deletion, subject to legal, contractual, technical, and operational limits.',
      'Where GDPR applies, our lawful bases may include consent, performance of a contract or requested service, legitimate interests in operating and improving the tool, and compliance with legal obligations. The applicable lawful basis depends on the specific processing activity.',
      'To exercise privacy rights, ask questions, or revoke consent where applicable, contact: chris@it3dp.ca'
    ]
  },
  {
    heading: 'Equipment Information, Pricing, and Availability',
    body: [
      'Equipment images, specifications, dimensions, clearances, prices, and catalog information shown in the tool are provided for layout visualization and planning discussion only.',
      'Equipment information may be incomplete, approximate, outdated, or subject to manufacturer changes. You must verify all specifications, dimensions, compatibility, availability, pricing, installation requirements, and safety requirements with current manufacturer documentation and an authorized equipment supplier representative before making any purchase or installation decision.',
      'Information shown in the tool does not constitute an offer to sell, a quote, a guarantee of availability, or a representation that a specific product is suitable for your site or intended use.'
    ]
  },
  {
    heading: 'Intellectual Property',
    body: [
      'Layouts you create using ShopBuilder Pro belong to you, subject to any rights in the underlying tool, templates, equipment catalog data, images, brand assets, and third-party materials.',
      'The ShopBuilder Pro application, source code, interface, design elements, brand assets, equipment catalog data, and related materials remain the property of iTech or their respective owners.',
      'You may use exported layouts for internal planning and discussions with qualified professionals, but you may not copy, reverse engineer, resell, redistribute, or create a competing product from the ShopBuilder Pro application, catalog data, or source materials unless expressly authorized in writing.'
    ]
  },
  {
    heading: 'Changes to These Terms',
    body: [
      'We may update these terms as the tool evolves. When we make material changes, you may be prompted to review and accept the updated version before continuing to use ShopBuilder Pro.',
      'Continued use of ShopBuilder Pro after updated terms are presented constitutes acceptance of the updated terms.'
    ]
  }
];

// ============================================================================
// MAIN COMPONENT
// Two modes:
//   - Gate mode (forceOpen=false, onClose=null): blocks app entry, requires acceptance
//   - Review mode (forceOpen=true, onClose=fn): just shows terms with a Close button
// ============================================================================

export default function TermsModal({ onAccept, forceOpen = false, onClose = null }) {
  // User must scroll through terms before Accept becomes enabled (gate mode only)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Granular consent — both checkboxes required (per GDPR best practice)
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeData, setAgreeData] = useState(false);

  // Detect scroll-to-bottom with 20px tolerance (exact bottom is fiddly with rounding)
  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 20) {
      setHasScrolledToBottom(true);
    }
  };

  // Save acceptance, then notify parent
  const handleAccept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: CURRENT_TERMS_VERSION,
        accepted: true,
        acceptedAt: new Date().toISOString()
      }));
    } catch {
      // localStorage disabled (private browsing etc.) — still allow this session
      console.warn('Could not save terms acceptance');
    }
    if (onAccept) onAccept();
  };

  const isReviewMode = forceOpen && onClose;
  const canAccept = agreeTerms && agreeData && hasScrolledToBottom;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            {isReviewMode ? 'Terms of Service & Privacy Policy' : 'Welcome to ShopBuilder Pro'}
          </h2>
          <div style={styles.subtitle}>
            Version {CURRENT_TERMS_VERSION} · Please review before continuing
          </div>
        </div>

        {/* Scrollable terms body */}
        <div style={styles.scrollArea} onScroll={handleScroll}>
          <div style={styles.disclaimer}>
            <strong>Important:</strong> ShopBuilder Pro is a DIY planning tool, not a substitute
            for professional CAD, engineering, or architectural services. Please read carefully.
          </div>

          {TERMS_SECTIONS.map((section, idx) => (
            <div key={idx} style={styles.section}>
              <h3 style={styles.sectionHeading}>{section.heading}</h3>
              {section.body.map((para, pIdx) => (
                <p key={pIdx} style={styles.paragraph}>{para}</p>
              ))}
            </div>
          ))}

          <div style={styles.contactFooter}>
            Questions or to exercise data rights: <span style={styles.email}>chris@it3dp.ca</span>
          </div>
        </div>

        {/* Scroll hint — gate mode, before scrolled */}
        {!isReviewMode && !hasScrolledToBottom && (
          <div style={styles.scrollHint}>↓ Scroll to read all terms before accepting</div>
        )}

        {/* Gate mode: checkboxes + Accept button */}
        {!isReviewMode && (
          <div style={styles.actions}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                style={styles.checkbox}
              />
              <span>I understand this is a DIY planning tool, not a substitute for professional CAD or engineered drawings, and I accept the Terms of Service.</span>
            </label>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={agreeData}
                onChange={(e) => setAgreeData(e.target.checked)}
                style={styles.checkbox}
              />
              <span>I consent to local data storage (cookies/localStorage) and the privacy practices described above.</span>
            </label>

            <button
              onClick={handleAccept}
              disabled={!canAccept}
              style={{ ...styles.acceptButton, ...(canAccept ? {} : styles.acceptButtonDisabled) }}
            >
              {canAccept ? 'Accept & Continue' : 'Please read and check both boxes to continue'}
            </button>
          </div>
        )}

        {/* Review mode: just a Close button */}
        {isReviewMode && (
          <div style={styles.actions}>
            <button onClick={onClose} style={styles.closeButton}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// STYLES — navy aesthetic, inline per project convention.
// Uses theme tokens where available, falls back to hardcoded navy values.
// ============================================================================
const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000, // above everything else
    padding: '20px'
  },
  modal: {
    backgroundColor: T?.bgPanel || '#1a2942',
    border: `1px solid ${T?.border || '#2a3f5f'}`,
    borderRadius: '8px',
    maxWidth: '720px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    color: T?.text || '#e0e6f0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: `1px solid ${T?.border || '#2a3f5f'}`
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: T?.textBright || '#ffffff'
  },
  subtitle: {
    marginTop: '4px',
    fontSize: '12px',
    color: T?.textDim || '#8a9bb8'
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px',
    fontSize: '13px',
    lineHeight: 1.5
  },
  disclaimer: {
    backgroundColor: 'rgba(255, 153, 0, 0.1)',
    border: '1px solid rgba(255, 153, 0, 0.3)',
    borderRadius: '4px',
    padding: '12px 14px',
    marginBottom: '20px',
    color: T?.textBright || '#ffffff'
  },
  section: { marginBottom: '20px' },
  sectionHeading: {
    fontSize: '14px',
    fontWeight: 600,
    color: T?.textBright || '#ffffff',
    marginBottom: '8px',
    marginTop: 0
  },
  paragraph: {
    margin: '0 0 8px 0',
    color: T?.text || '#e0e6f0'
  },
  contactFooter: {
    marginTop: '24px',
    padding: '12px 0',
    fontSize: '12px',
    color: T?.textDim || '#8a9bb8',
    borderTop: `1px solid ${T?.border || '#2a3f5f'}`
  },
  email: { color: T?.accent || '#5b8def' },
  scrollHint: {
    padding: '8px 24px',
    fontSize: '12px',
    color: T?.textDim || '#8a9bb8',
    textAlign: 'center',
    backgroundColor: 'rgba(255, 153, 0, 0.05)',
    borderTop: `1px solid ${T?.border || '#2a3f5f'}`
  },
  actions: {
    padding: '16px 24px 20px',
    borderTop: `1px solid ${T?.border || '#2a3f5f'}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    fontSize: '13px',
    cursor: 'pointer',
    color: T?.text || '#e0e6f0',
    lineHeight: 1.4
  },
  checkbox: { marginTop: '2px', cursor: 'pointer', flexShrink: 0 },
  acceptButton: {
    marginTop: '6px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: T?.accent || '#5b8def',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  acceptButtonDisabled: {
    backgroundColor: T?.bgInput || '#2a3f5f',
    color: T?.textDim || '#8a9bb8',
    cursor: 'not-allowed'
  },
  closeButton: {
    padding: '10px 20px',
    fontSize: '14px',
    backgroundColor: T?.bgInput || '#2a3f5f',
    color: T?.text || '#e0e6f0',
    border: `1px solid ${T?.border || '#3a557f'}`,
    borderRadius: '4px',
    cursor: 'pointer',
    alignSelf: 'flex-end'
  }
};